import React, {
  FunctionComponent,
  useContext,
  useCallback,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  PageHeader,
  Form,
  Button,
  ButtonProps,
  ResponsivePadding,
} from '../../components';
import { AccountFields } from './AccountFields';
import { AuthStore, updateAuthUser, BrandStore } from '../../context';
import {
  mapUserToInitialFormValues,
  mapFormValuesToPayload,
  validateFormValues,
} from './AccountFields.util';
import { AccountFieldsValues } from './AccountFields.types';
import { coreapi } from '../../api';
import { history } from '../../history';
import { useToaster } from '../../hooks';
import {
  TermsAndConditionsModal,
  useTocHelper,
} from '../welcome/TermsAndConitionsModal';
import { PrimaryButton } from '@stax-core/truffle-components';
const LinkButton: FunctionComponent<ButtonProps> = props => (
  <Button variant="link" inline style={{ margin: 0 }} {...props} />
);

const StyledInfo = styled.div`
  padding: 8px;
  background: #f2f2f2;
  margin-top: 16px;
`;

export const AccountPage2: FunctionComponent = () => {
  const {
    dispatch,
    state: { auth },
  } = useContext(AuthStore);
  const {
    state: { brand },
  } = useContext(BrandStore);
  const { toaster, toast } = useToaster();
  const [showToc, setShowToc] = useState(false);
  const { registerAcceptance } = useTocHelper();

  const authToken = auth && auth.token;
  const { regPasswordHeader, regPasswordBody } = brand || {};
  const sendWelcomeEmail = brand?.sendWelcomeEmail;

  const onSubmit = useCallback(
    async (formValues: AccountFieldsValues) => {
      const payload = mapFormValuesToPayload(formValues);

      try {
        const user = await coreapi.put(authToken!, '/self', {
          ...payload,
          // only set flag if merchant's brand have the flag setWelcomeEmail active in AirTable
          // flag is used to conditionally sent user a welcome email
          send_welcome_email: sendWelcomeEmail,
          mfa_enabled: brand?.displayName === 'Stax' ? true : undefined,
        });
        dispatch(updateAuthUser(user));
        await registerAcceptance();
        history.push('/application/business');
      } catch (error) {
        toaster(
          toast.error(error, 'There was a problem setting your password.'),
        );
      }
    },
    [
      authToken,
      dispatch,
      toaster,
      toast,
      sendWelcomeEmail,
      registerAcceptance,
      brand,
    ],
  );

  return (
    <>
      <PageHeader
        header={regPasswordHeader}
        id="account-page-header"
        data-testid="account-page-header"
        description={<p>{regPasswordBody}</p>}
      />
      <ResponsivePadding>
        <Form
          initialValues={mapUserToInitialFormValues(auth?.user)}
          validate={validateFormValues}
          onSubmit={onSubmit}
        >
          {formProps => (
            <AccountFields mode="edit" {...formProps}>
              <StyledInfo>
                By clicking continue you agree to provide us with information to
                comply with requirements from regulators, financial partners and
                the terms of our{' '}
                <LinkButton
                  data-testid="show-toc-button"
                  onClick={_event => setShowToc(true)}
                >
                  Services Agreement
                </LinkButton>
                .
              </StyledInfo>
              <TermsAndConditionsModal
                showToc={showToc}
                setShowToc={setShowToc}
              />

              <PrimaryButton
                type="submit"
                style={{ width: '100%' }}
                id="account-continue-button"
                data-testid="account-continue-button"
                trackingId="account-continue-button"
                disabled={formProps.submitting}
              >
                Save &amp; Continue
              </PrimaryButton>
            </AccountFields>
          )}
        </Form>
      </ResponsivePadding>
    </>
  );
};
